import { Component, inject, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, map, catchError } from 'rxjs/operators';
import { forkJoin, of, Observable } from 'rxjs';

import { NachaParserService } from '../../services/nacha-parser.service';
import { NachaWizardService } from '../../services/nacha-wizard.service';
import { PaymentCollectStore } from '../../store/payment-collect.store';
import { ModalService, ModalComponentInterface } from 'shared/components/modal/modal.service';
import { NotificationService } from 'shared/services/notification.service';
import { CustomersService } from '../../../customer/services/customers.service';
import { TransactionsService } from '../../../transactions/services/transactions.service';
import { SecureClientService } from 'app/secure-client/secure-client.service';
import { CustomerDetailsFormComponent } from '../../../customer/components/customer-details-form/customer-details-form.component';
import { CustomerModel, AchMethodModel } from '../../../customer/customer.model';

import { NachaParseResult, NachaMatch, NachaResult } from '../../models/nacha.model';
import { NachaUploadStepComponent } from './steps/upload-step.component';
import { NachaReviewStepComponent } from './steps/review-step.component';
import { NachaProcessStepComponent } from './steps/process-step.component';
import { NachaResultsStepComponent } from './steps/results-step.component';
import { NachaCustomerSelectorModalComponent } from './modals/customer-selector-modal.component';
import { NachaAchTokenSelectorModalComponent } from './modals/ach-token-selector-modal.component';
import { NachaConfirmCreateAchTokenModalComponent } from './modals/confirm-create-ach-token-modal.component';

export enum NachaStep {
  UPLOAD = 0,
  REVIEW = 1,
  PROCESS = 2,
  RESULTS = 3,
}

@Component({
  selector: 'app-nacha-wizard',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    NachaUploadStepComponent,
    NachaReviewStepComponent,
    NachaProcessStepComponent,
    NachaResultsStepComponent,
  ],
  templateUrl: './nacha-wizard.component.html',
})
export class NachaWizardComponent implements OnInit {
  protected nachaParser = inject(NachaParserService);
  protected nachaWizardService = inject(NachaWizardService);
  protected paymentCollectStore = inject(PaymentCollectStore);
  protected modalService = inject(ModalService);
  protected customersService = inject(CustomersService);
  protected transactionsService = inject(TransactionsService);
  protected secureClient = inject(SecureClientService);
  protected notificationService = inject(NotificationService);
  protected destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  // Modal wrapper data (unused but required by ModalService interface)
  data: any;

  NachaStep = NachaStep;
  currentStep = NachaStep.UPLOAD;

  // Parse result from upload step
  parseResult: NachaParseResult | null = null;
  // Remembers the uploaded file's display name so the upload step can
  // restore it after being destroyed/recreated by @if when navigating back.
  uploadedFileName: string | null = null;

  // Customer matches for review step
  matches: NachaMatch[] = [];
  isMatching = false;

  // Processing state
  isProcessing = false;
  processingProgress = 0;

  // Final results
  results: NachaResult[] = [];

  // Step gating
  canProceedToReview = false;
  canProceedToProcess = false;

  ngOnInit(): void {
    // Retired customers are no longer loaded by default, and matching against a partial book
    // silently creates a duplicate for every retired account it fails to recognise. No-op once
    // the sweep has run. Loads the active book first if nothing has yet.
    this.paymentCollectStore.ensureCustomersLoaded({ includeInactive: true });
  }

  // ── Step 1: Upload ─────────────────────────────────────────────────────────

  onNachaUploaded(event: { fileName: string; content: string }): void {
    if (!event.content) {
      this.parseResult = null;
      this.canProceedToReview = false;
      this.uploadedFileName = null;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.parseResult = this.nachaParser.parseNacha(event.content);
      this.canProceedToReview = (this.parseResult?.rows.length ?? 0) > 0;
      this.uploadedFileName = event.fileName;
    } catch {
      this.parseResult = null;
      this.canProceedToReview = false;
      this.uploadedFileName = null;
    }
    this.cdr.detectChanges();
  }

  proceedToReview(): void {
    if (!this.canProceedToReview || !this.parseResult) return;

    this.isMatching = true;
    this.currentStep = NachaStep.REVIEW;
    this.cdr.detectChanges();

    const customers = this.paymentCollectStore.data.customers() || [];

    this.nachaWizardService.matchCustomers(this.parseResult.rows, customers).subscribe({
      next: (matches) => {
        this.matches = matches;
        this.isMatching = false;
        this.updateCanProceedToProcess();
        this.cdr.detectChanges();
      },
      error: () => {
        this.isMatching = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Step 2: Review actions ─────────────────────────────────────────────────

  onToggleInclude(match: NachaMatch): void {
    match.include = !match.include;
    this.updateCanProceedToProcess();
    this.cdr.detectChanges();
  }

  onAddCustomer(match: NachaMatch): void {
    this.modalService.openModal({
      title: 'Add New Customer',
      body: CustomerDetailsFormComponent,
      data: {
        customerData: {
          companyAccountNumber: match.row.individualIdNumber,
          companyName: match.row.individualName,
          displayName: match.row.individualName,
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => modal.close(),
      },
      confirm: {
        label: 'Save Customer',
        action: (modal: any, customerFormComponent: CustomerDetailsFormComponent) => {
          const form = customerFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const formData = form.getRawValue();
          const customerModel: CustomerModel = {
            contactFirstName: formData!.contactFirstName ?? undefined,
            contactLastName: formData!.contactLastName ?? undefined,
            displayName: formData!.displayName ?? undefined,
            companyName: formData!.companyName ?? undefined,
            companyAccountNumber: formData!.companyAccountNumber ?? undefined,
            contactEmail: formData!.contactEmail ?? undefined,
            contactPhoneNumber: formData!.businessPhone + ' ' + formData!.businessPhoneExt,
            mobilePhoneNumber: formData!.mobilePhoneNumber ?? undefined,
          };

          if (!customerModel.contactEmail || customerModel.contactEmail === '') delete customerModel.contactEmail;
          if (customerModel.contactPhoneNumber === ' ' || customerModel.contactPhoneNumber === '')
            delete customerModel.contactPhoneNumber;
          if (customerModel.mobilePhoneNumber === '' || customerModel.mobilePhoneNumber === ' ')
            delete customerModel.mobilePhoneNumber;
          if (customerModel.contactFirstName === '') delete customerModel.contactFirstName;
          if (customerModel.contactLastName === '') delete customerModel.contactLastName;

          this.customersService.createCustomer(customerModel).subscribe({
            next: (newCustomer) => {
              this.notificationService.success('Customer created successfully');
              this.paymentCollectStore.loadCustomers();
              this.updateMatchWithCustomer(match, newCustomer);
              modal.close();
            },
            error: () => {
              this.notificationService.error('Failed to create customer');
            },
          });
        },
      },
    });
  }

  /** Create a customer (from the NACHA row) for every "Customer not found" row at once. */
  onAddAllCustomers(): void {
    const unmatched = this.matches.filter((m) => m.status === 'no-customer');
    if (unmatched.length === 0) return;

    forkJoin(
      unmatched.map((match) =>
        this.customersService.createCustomer(this.buildCustomerFromRow(match)).pipe(
          map((newCustomer) => ({ match, newCustomer })),
          catchError(() => of(null))
        )
      )
    ).subscribe({
      next: (results) => {
        const created = results.filter((r): r is { match: NachaMatch; newCustomer: CustomerModel } => !!r);
        created.forEach(({ match, newCustomer }) => this.updateMatchWithCustomer(match, newCustomer));
        if (created.length > 0) this.paymentCollectStore.loadCustomers();

        const failed = results.length - created.length;
        if (failed === 0) {
          this.notificationService.success(`Created ${created.length} customers`);
        } else {
          this.notificationService.error(
            `Created ${created.length} of ${results.length} customers — ${failed} failed`
          );
        }
        this.cdr.detectChanges();
      },
      error: () => this.notificationService.error('Failed to create customers'),
    });
  }

  /** Minimal customer model pre-filled from a NACHA row (matches the "Add Customer" pre-fill). */
  private buildCustomerFromRow(match: NachaMatch): CustomerModel {
    return {
      companyAccountNumber: match.row.individualIdNumber,
      companyName: match.row.individualName,
      displayName: match.row.individualName,
    };
  }

  onSelectCustomer(match: NachaMatch): void {
    const customers = this.paymentCollectStore.data.customers() || [];
    this.modalService.openModal({
      title: 'Select Customer',
      body: NachaCustomerSelectorModalComponent,
      data: { match, customers },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => modal.close(),
      },
      confirm: {
        label: 'Select Customer',
        action: (modal: any, selectorComponent: NachaCustomerSelectorModalComponent) => {
          if (!selectorComponent.selectedCustomer) return;
          this.updateMatchWithCustomer(match, selectorComponent.selectedCustomer);
          modal.close();
        },
      },
    });
  }

  private updateMatchWithCustomer(match: NachaMatch, customer: CustomerModel): void {
    match.customer = customer;
    match.status = 'matched';
    match.error = undefined;
    this.updateCanProceedToProcess();
    this.cdr.detectChanges();
  }

  // ── Step 2: ACH account conflict resolution ─────────────────────────────────

  /** Charge the account from the NACHA file (one-time). */
  onUseNacha(match: NachaMatch): void {
    this.applyUseNacha(match);
  }

  /** Charge the customer's account on file; prompt to pick when there are several. */
  onUseFreely(match: NachaMatch): void {
    const tokens = match.achTokens ?? [];
    if (tokens.length === 0) return;
    if (tokens.length === 1) {
      this.applyUseFreely(match, tokens[0]);
      return;
    }

    this.modalService.openModal({
      title: 'Select Account on File',
      body: NachaAchTokenSelectorModalComponent,
      data: { achTokens: tokens, match },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => modal.close(),
      },
      confirm: {
        label: 'Use Account',
        action: (modal: any, selector: NachaAchTokenSelectorModalComponent) => {
          if (!selector.selectedToken) return;
          this.applyUseFreely(match, selector.selectedToken as AchMethodModel);
          modal.close();
        },
      },
    });
  }

  /** Replace the on-file account with the NACHA account (add new, delete old), then charge it. */
  onUpdateFreely(match: NachaMatch): void {
    this.modalService.openModal({
      title: 'Update Account on File',
      body: NachaConfirmCreateAchTokenModalComponent,
      data: { row: match.row, customerName: this.customerLabel(match) },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => modal.close(),
      },
      confirm: {
        label: 'Update Account',
        action: (modal: any) => {
          this.replaceOnFileAccount(match).subscribe({
            next: () => {
              this.notificationService.success('Account on file updated');
              modal.close();
            },
            error: () => {
              this.notificationService.error('Failed to update account on file');
            },
          });
        },
      },
    });
  }

  /** Save the NACHA account to the customer's file (no prior account), then charge it. */
  onSaveOnFile(match: NachaMatch): void {
    this.saveOnFileAccount(match).subscribe({
      next: () => this.notificationService.success('Account saved to customer file'),
      error: () => this.notificationService.error('Failed to save account on file'),
    });
  }

  /** Apply one resolution to every account-mismatch row. */
  onApplyToAllMismatches(action: 'nacha' | 'freely' | 'update'): void {
    const mismatches = this.matches.filter((m) => m.status === 'account-mismatch');
    if (mismatches.length === 0) return;

    if (action === 'nacha') {
      mismatches.forEach((m) => this.applyUseNacha(m));
      return;
    }

    if (action === 'freely') {
      mismatches.forEach((m) => {
        const tokens = m.achTokens ?? [];
        const token = tokens.find((t) => t.isDefault) ?? tokens[0];
        if (token) this.applyUseFreely(m, token);
      });
      return;
    }

    // action === 'update' — replace every on-file account with its NACHA account.
    forkJoin(
      mismatches.map((m) => this.replaceOnFileAccount(m).pipe(catchError(() => of(null))))
    ).subscribe({
      next: () => {
        this.notificationService.success('Accounts on file updated');
        this.cdr.detectChanges();
      },
      error: () => this.notificationService.error('Some accounts could not be updated'),
    });
  }

  private applyUseNacha(match: NachaMatch): void {
    match.accountSource = 'nacha';
    match.selectedToken = undefined;
    this.resolveMismatch(match);
  }

  private applyUseFreely(match: NachaMatch, token: AchMethodModel): void {
    match.selectedToken = token;
    match.accountSource = 'freely';
    this.resolveMismatch(match);
  }

  /**
   * Tokenize the NACHA account, delete the customer's stale ACH token(s), then charge
   * the freshly created token. Returns once the new token is resolved and selected.
   */
  private replaceOnFileAccount(match: NachaMatch): Observable<void> {
    const customerId = match.customer!.id!;
    const oldIds = (match.achTokens ?? [])
      .map((t) => t.id)
      .filter((id): id is string => !!id);
    const payload = this.nachaWizardService.buildAchTokenPayload(match.row);

    return this.secureClient.addAchToken(customerId, payload).pipe(
      switchMap(() => this.customersService.getAchPaymentMethods(customerId)),
      switchMap((tokens) => {
        const newToken = this.pickNewToken(match, tokens);
        const toDelete = oldIds.filter((id) => id !== newToken?.id);
        const deletes: Observable<unknown> = toDelete.length
          ? forkJoin(
              toDelete.map((id) =>
                this.customersService.deletePaymentMethod(customerId, id).pipe(catchError(() => of(null)))
              )
            )
          : of(null);

        return deletes.pipe(
          map(() => {
            match.achTokens = tokens.filter((t) => !toDelete.includes(t.id ?? ''));
            if (newToken) this.applyUseFreely(match, newToken);
          })
        );
      })
    );
  }

  /** Tokenize the NACHA account and add it to the customer's file, then charge it. */
  private saveOnFileAccount(match: NachaMatch): Observable<void> {
    const customerId = match.customer!.id!;
    const payload = this.nachaWizardService.buildAchTokenPayload(match.row);

    return this.secureClient.addAchToken(customerId, payload).pipe(
      switchMap(() => this.customersService.getAchPaymentMethods(customerId)),
      map((tokens) => {
        match.achTokens = tokens;
        const newToken = this.pickNewToken(match, tokens);
        if (newToken) this.applyUseFreely(match, newToken);
      })
    );
  }

  /** Choose the just-created token from a refreshed list (exact match → default → newest). */
  private pickNewToken(match: NachaMatch, tokens: AchMethodModel[]): AchMethodModel | undefined {
    return (
      tokens.find((t) => this.nachaWizardService.achAccountMatches(match.row, t)) ??
      tokens.find((t) => t.isDefault) ??
      tokens[tokens.length - 1]
    );
  }

  private customerLabel(match: NachaMatch): string {
    return (
      match.customer?.displayName ||
      match.customer?.companyName ||
      match.row.individualName ||
      'Customer'
    );
  }

  private resolveMismatch(match: NachaMatch): void {
    match.status = 'matched';
    match.error = undefined;
    this.updateCanProceedToProcess();
    this.cdr.detectChanges();
  }

  private updateCanProceedToProcess(): void {
    this.canProceedToProcess = this.matches.some((m) => m.include && m.status === 'matched' && m.row.amount > 0);
  }

  // ── Step 3: Process ────────────────────────────────────────────────────────

  proceedToProcess(): void {
    if (!this.canProceedToProcess) return;

    this.isProcessing = true;
    this.processingProgress = 0;
    this.currentStep = NachaStep.PROCESS;
    this.cdr.detectChanges();

    this.nachaWizardService.processBulkAchPayments(this.matches).subscribe({
      next: (results) => {
        this.results = results;
        this.isProcessing = false;
        this.processingProgress = 100;
        this.cdr.detectChanges();

        setTimeout(() => {
          this.currentStep = NachaStep.RESULTS;
          this.cdr.detectChanges();
        }, 500);
      },
      error: () => {
        this.isProcessing = false;
        this.notificationService.error('An error occurred while processing payments');
        this.cdr.detectChanges();
      },
    });
  }

  // ── Step 4: Results ────────────────────────────────────────────────────────

  downloadResults(): void {
    const csvContent = this.nachaWizardService.generateResultsCsv(this.matches, this.results);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    this.nachaWizardService.downloadCsv(csvContent, `nacha-results-${timestamp}.csv`);
  }

  viewReceipt(transactionId: string): void {
    this.transactionsService
      .getTransaction(transactionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((details) => this.transactionsService.getHTMLReceipt(details))
      )
      .subscribe({
        next: (htmlContent) => {
          const win = window.open(
            ' ',
            '_blank',
            'width=500,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
          );
          if (win) {
            win.document.write(htmlContent);
            win.document.close();
          }
        },
        error: () => this.notificationService.error('Failed to load receipt'),
      });
  }

  downloadReceipt(transactionId: string): void {
    this.transactionsService
      .getTransaction(transactionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => this.transactionsService.getPdfReceipt(details),
        error: () => this.notificationService.error('Failed to download receipt'),
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  goBack(): void {
    if (this.currentStep > NachaStep.UPLOAD) {
      this.currentStep--;
    }
  }

  reset(): void {
    this.currentStep = NachaStep.UPLOAD;
    this.parseResult = null;
    this.uploadedFileName = null;
    this.matches = [];
    this.results = [];
    this.isMatching = false;
    this.isProcessing = false;
    this.processingProgress = 0;
    this.canProceedToReview = false;
    this.canProceedToProcess = false;
    this.cdr.detectChanges();
  }

  get includedCount(): number {
    return this.matches.filter((m) => m.include && m.status === 'matched').length;
  }

  get successCount(): number {
    return this.results.filter((r) => r.status === 'success').length;
  }

  get failedCount(): number {
    return this.results.filter((r) => r.status === 'failed').length;
  }
}
